import React from 'react';
import style from './passwordStrengthAssessment.module.scss';
import errorStyle from './errorsDisplay.module.scss';
import classNames from 'classnames';
import { LoadingIndicator } from 'acscp/components/common/loadingIndicator';
import { defineMessages, FormattedMessage, InjectedIntlProps } from 'react-intl';
import { MINIMUM_PASSWORD_LENGTH, MINIMUM_PASSWORD_STRENGTH } from 'acscp/settings/appSettings';

const messages = defineMessages({
    passwordLengthError: {
        id: 'web.components.pages.passwordReset.passwordMinLength',
        defaultMessage: 'Passwords must be {minLength} characters or more.'
    },
    strength0: {
        id: 'components.common.passwordStrengthAssessment.veryWeakPassword',
        defaultMessage: 'Password is too weak'
    },
    strength1: {
        id: 'components.common.passwordStrengthAssessment.weakPassword',
        defaultMessage: 'Password is too weak'
    },
    strength2: {
        id: 'components.common.passwordStrengthAssessment.fairPassword',
        defaultMessage: 'Fair Password'
    },
    strength3: {
        id: 'components.common.passwordStrengthAssessment.goodPassword',
        defaultMessage: 'Good Password'
    },
    strength4: {
        id: 'components.common.passwordStrengthAssessment.strongPassword',
        defaultMessage: 'Strong Password'
    },
});

interface PasswordStrengthAssessmentProps extends InjectedIntlProps {
    password: string;
    passwordStrengthCallback: (strength: boolean) => void;
}

type zxcvbnFunc = (password: string, userInputs?: string[] | undefined) => zxcvbn.ZXCVBNResult;

interface AssessmentResult {
    score: number;
    suggestions: string[];
}

interface PasswordStrengthAssessmentState {
    validate?: zxcvbnFunc;
    result?: AssessmentResult;
}

class PasswordStrengthAssessment extends React.Component<PasswordStrengthAssessmentProps,
                                                         PasswordStrengthAssessmentState> {

    private canModifyState: boolean;

    constructor(props: PasswordStrengthAssessmentProps) {
        super(props);
        this.state = {};
    }

    componentDidMount(): void {
        this.canModifyState = true;

        const zxcvbnPromise = import('zxcvbn');
        zxcvbnPromise.then(resolvedZxcvbn => {
            // Unwrap through nested `default` until we reach the callable.
            // Vite's CJS interop can produce `{ default: { default: fn } }` for zxcvbn.
            let candidate: any = resolvedZxcvbn;
            while (candidate && typeof candidate !== 'function' && candidate.default) {
                candidate = candidate.default;
            }
            const zxcvbn = candidate as zxcvbnFunc;

            // Normally, async operations should be cancelled on component unmount
            // to prevent setting state on unmounted components.
            // The standard Promise implementation does not provide cancellation,
            // so we use a property to check if the component's state can be changed.
            if (this.canModifyState) {
                this.setState(
                    {validate: zxcvbn},
                    () => this.assessPassword(this.props.password)
                );
            }
        });
    }

    componentWillUnmount(): void {
        this.canModifyState = false;
    }

    componentWillReceiveProps(nextProps: PasswordStrengthAssessmentProps): void {
        if (this.props.password !== nextProps.password) {
            this.assessPassword(nextProps.password);
        }
    }

    private assessPassword(password: string): void {
        if (password.length < MINIMUM_PASSWORD_LENGTH) {
            const lengthError = this.props.intl.formatMessage(
                messages.passwordLengthError,
                {minLength: MINIMUM_PASSWORD_LENGTH}
            );
            this.setValidAssessmentResult(0, [lengthError]);
        } else {
            if (!this.state.validate) {
                this.setUnavailableAssessmentResult();
                return;
            }

            const result = this.state.validate(password);

            const suggestionsAndWarnings = [...result.feedback.suggestions];
            if (result.feedback.warning) {
                suggestionsAndWarnings.unshift(result.feedback.warning);
            }

            this.setValidAssessmentResult(result.score, suggestionsAndWarnings);
        }
    }

    private setValidAssessmentResult(score: number, suggestions: string[]): void {
        this.props.passwordStrengthCallback(score >= MINIMUM_PASSWORD_STRENGTH);
        this.setState({
            result: { score, suggestions }
        });
    }

    private setUnavailableAssessmentResult(): void {
        this.props.passwordStrengthCallback(false);
        this.setState({result: undefined});
    }

    private strengthStyle(score: number): any {
        switch (score) {
            case 1:
                return style.Score1;
            case 2:
                return style.Score2;
            case 3:
                return style.Score3;
            case 4:
                return style.Score4;
            case 0:
            default:
                return style.Score0;
        }
    }

    private strengthDescription(score: number): FormattedMessage.MessageDescriptor {
        switch (score) {
            case 4:
                return messages.strength4;
            case 3:
                return messages.strength3;
            case 2:
                return messages.strength2;
            case 1:
                return messages.strength1;
            case 0:
            default:
                return messages.strength0;
        }
    }

    render(): JSX.Element | null {
        // Don't show any errors for absent input.
        if (this.props.password.length === 0) {
            return null;
        }

        if (!this.state.result) {
            return <LoadingIndicator/>;
        }

        const {score, suggestions} = this.state.result;

        const strengthDescription = this.props.intl.formatMessage(this.strengthDescription(score));

        return (
            <div className={classNames(style.PasswordStrength, style.RaisedSpecificity)}>
                <div className={errorStyle.UpArrow} />
                <ul className={classNames(errorStyle.Error, style.StrengthMeter)}>
                    <li className={classNames(style.Assessment, this.strengthStyle(score))}>
                        <span>{strengthDescription}</span>
                    </li>
                    {suggestions.map(suggestion =>
                        <li className={style.Suggestion} key={suggestion.substring(5)}>{suggestion}</li>
                    )}
                </ul>
            </div>
        );
    }
}
export { PasswordStrengthAssessment };
